import type { Editor } from "tinymce";

// Event constants
export const PURE_TONE_AUDIOGRAM_REPORT_SELECTED =
  "patient-audiology:pure_tone_audiogram_report_selected" as const;

export const SPEECH_AUDIOLOGY_WORD_RECOGNITION_REPORT_SELECTED =
  "patient-audiology:speech_audiometry_word_recognition_report_selected" as const;

export const TYMPANOMETRY_REPORT_SELECTED =
  "patient-audiology:tympanometry_report_selected" as const;

export const ACOUSTIC_REFLEX_DECAY_REPORT_SELECTED =
  "patient-audiology:acoustic_reflex_decay_report_selected" as const;

export const SPEECH_IN_NOISE_REPORT_SELECTED =
  "patient-audiology:speech_in_noise_report_selected" as const;

export const OTOACOUSTIC_EMISSIONS_REPORT_SELECTED =
  "patient-audiology:otoacoustic_emissions_report_selected" as const;

export const SPEECH_IN_NOISE_REPORT_UPDATE_METHOD =
  "patient-audiology:speech_in_noise_report_update_method" as const;

export const SPEECH_AUDIOMETRY_REPORT_UPDATE_PRESENTATION =
  "patient-audiology:speech_audiometry_report_update_presentation" as const;

export const WORD_RECOGNITION_REPORT_UPDATE_PRESENTATION =
  "patient-audiology:word_recognition_report_update_presentation" as const;

export const SPEECH_AUDIOMETRY_REPORT_UPDATE_WORD_LIST =
  "patient-audiology:speech_audiometry_report_update_word_list" as const;

export const WORD_RECOGNITION_REPORT_UPDATE_WORD_LIST =
  "patient-audiology:word_recognition_report_update_word_list" as const;

export const REPORT_APPOINTMENT_CHANGED =
  "patient-audiology:report_appointment_changed" as const;

export const LAST_FOCUSED_WORD_PROCESSOR_FIELD =
  "word-processor:last_focused_field" as const;

export const AMBIENT_SCRIBE_REFETCH = "ambient-scribe:refetch" as const;

export type AudiologyValuePayload = {
  groupName: string;
  value: string;
};

export type AudiologyWordListPayload = {
  groupName: string;
  value: number | null;
};

export type ReportSelectedPayload = {
  value: boolean;
};

export type ReportAppointmentChangedPayload = {
  providerId: number | undefined;
};

// Event type mapping
export type EventBusEventsType = {
  [PURE_TONE_AUDIOGRAM_REPORT_SELECTED]: ReportSelectedPayload;
  [SPEECH_AUDIOLOGY_WORD_RECOGNITION_REPORT_SELECTED]: ReportSelectedPayload;
  [TYMPANOMETRY_REPORT_SELECTED]: ReportSelectedPayload;
  [ACOUSTIC_REFLEX_DECAY_REPORT_SELECTED]: ReportSelectedPayload;
  [SPEECH_IN_NOISE_REPORT_SELECTED]: ReportSelectedPayload;
  [OTOACOUSTIC_EMISSIONS_REPORT_SELECTED]: ReportSelectedPayload;
  [SPEECH_IN_NOISE_REPORT_UPDATE_METHOD]: AudiologyValuePayload;
  [SPEECH_AUDIOMETRY_REPORT_UPDATE_PRESENTATION]: AudiologyValuePayload;
  [WORD_RECOGNITION_REPORT_UPDATE_PRESENTATION]: AudiologyValuePayload;
  [SPEECH_AUDIOMETRY_REPORT_UPDATE_WORD_LIST]: AudiologyWordListPayload;
  [WORD_RECOGNITION_REPORT_UPDATE_WORD_LIST]: AudiologyWordListPayload;
  [REPORT_APPOINTMENT_CHANGED]: ReportAppointmentChangedPayload;
  [LAST_FOCUSED_WORD_PROCESSOR_FIELD]: Editor;
  [AMBIENT_SCRIBE_REFETCH]: void;
};

type EventKey = keyof EventBusEventsType;
type EventHandler<E extends EventKey> = (
  payload: EventBusEventsType[E]
) => void;

type UnsubscribeFn = () => void;

interface EventBusConfig {
  maxListeners?: number;
  enableLogging?: boolean;
  throwOnError?: boolean;
}

class EventBus {
  private listeners = new Map<EventKey, Set<EventHandler<any>>>();
  private onceListeners = new Map<EventKey, Set<EventHandler<any>>>();
  private readonly config: Required<EventBusConfig>;

  constructor(config: EventBusConfig = {}) {
    this.config = {
      maxListeners: config.maxListeners ?? 50,
      enableLogging: config.enableLogging ?? false,
      throwOnError: config.throwOnError ?? false
    };
  }

  /**
   * Subscribe to an event
   * @returns Unsubscribe function
   */
  on<E extends EventKey>(event: E, handler: EventHandler<E>): UnsubscribeFn {
    this.addListener(event, handler, this.listeners);
    return () => this.off(event, handler);
  }

  /**
   * Subscribe to an event that fires only once
   * @returns Unsubscribe function
   */
  once<E extends EventKey>(event: E, handler: EventHandler<E>): UnsubscribeFn {
    this.addListener(event, handler, this.onceListeners);
    return () => this.offOnce(event, handler);
  }

  /**
   * Unsubscribe from an event
   */
  off<E extends EventKey>(event: E, handler: EventHandler<E>): void {
    this.listeners.get(event)?.delete(handler);

    // Clean up empty sets
    if (this.listeners.get(event)?.size === 0) {
      this.listeners.delete(event);
    }
  }

  /**
   * Unsubscribe from a once event
   */
  private offOnce<E extends EventKey>(
    event: E,
    handler: EventHandler<E>
  ): void {
    this.onceListeners.get(event)?.delete(handler);

    if (this.onceListeners.get(event)?.size === 0) {
      this.onceListeners.delete(event);
    }
  }

  /**
   * Emit an event to all subscribed handlers
   */
  emit<E extends EventKey>(event: E, payload: EventBusEventsType[E]): void {
    if (this.config.enableLogging) {
      console.log(`[EventBus] Emitting "${event}"`, payload);
    }

    // Execute regular listeners
    this.executeHandlers(event, payload, this.listeners);

    // Execute and remove once listeners
    const onceHandlers = this.onceListeners.get(event);
    if (onceHandlers) {
      this.executeHandlers(event, payload, this.onceListeners);
      this.onceListeners.delete(event);
    }
  }

  /**
   * Remove all listeners for a specific event or all events
   */
  clear(event?: EventKey): void {
    if (event) {
      this.listeners.delete(event);
      this.onceListeners.delete(event);
      if (this.config.enableLogging) {
        console.log(`[EventBus] Cleared listeners for "${event}"`);
      }
    } else {
      this.listeners.clear();
      this.onceListeners.clear();
      if (this.config.enableLogging) {
        console.log("[EventBus] Cleared all listeners");
      }
    }
  }

  /**
   * Get the number of listeners for an event
   */
  listenerCount(event: EventKey): number {
    const regularCount = this.listeners.get(event)?.size ?? 0;
    const onceCount = this.onceListeners.get(event)?.size ?? 0;
    return regularCount + onceCount;
  }

  /**
   * Get all registered event names
   */
  eventNames(): EventKey[] {
    const events = new Set<EventKey>([
      ...this.listeners.keys(),
      ...this.onceListeners.keys()
    ]);
    return Array.from(events);
  }

  /**
   * Add a listener to the specified map
   */
  private addListener<E extends EventKey>(
    event: E,
    handler: EventHandler<E>,
    listenerMap: Map<EventKey, Set<EventHandler<any>>>
  ): void {
    if (!listenerMap.has(event)) {
      listenerMap.set(event, new Set());
    }

    const handlers = listenerMap.get(event)!;
    handlers.add(handler);

    // Check for potential memory leaks
    const totalListeners = this.listenerCount(event);
    if (totalListeners > this.config.maxListeners) {
      console.warn(
        `[EventBus] Event "${event}" has ${totalListeners} listeners. Possible memory leak?`
      );
    }

    if (this.config.enableLogging) {
      console.log(
        `[EventBus] Added listener for "${event}" (total: ${totalListeners})`
      );
    }
  }

  /**
   * Execute all handlers for an event
   */
  private executeHandlers<E extends EventKey>(
    event: E,
    payload: EventBusEventsType[E],
    listenerMap: Map<EventKey, Set<EventHandler<any>>>
  ): void {
    const handlers = listenerMap.get(event);
    if (!handlers) return;

    handlers.forEach((handler) => {
      try {
        handler(payload);
      } catch (error) {
        const errorMessage = `[EventBus] Error in handler for "${event}": ${
          error instanceof Error ? error.message : String(error)
        }`;

        if (this.config.throwOnError) {
          throw new Error(errorMessage);
        } else {
          console.error(errorMessage, error);
        }
      }
    });
  }
}

// Export singleton instance with default config
export const eventBus = new EventBus();

// Export factory for custom instances
export const createEventBus = (config?: EventBusConfig) => new EventBus(config);

/*

Basic usage:
const unsub = eventBus.on(PURE_TONE_AUDIOGRAM_REPORT_SELECTED, ({ value }) => {
  console.log('Report selected:', value);
});


Emit event:
eventBus.emit(PURE_TONE_AUDIOGRAM_REPORT_SELECTED, { value: true });

Cleanup:
unsub(); // or
eventBus.clear(PURE_TONE_AUDIOGRAM_REPORT_SELECTED); // or
eventBus.clear(); // clear all

*/
